import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
    ComposedChart, Area, AreaChart, ReferenceLine
} from 'recharts';
import { 
    ChevronLeft, ChevronRight, Settings, Edit3, Target, Lock, Download, 
    Printer, TrendingUp, TrendingDown, Users, DollarSign, Activity, Search, X,
    Filter, Eye, EyeOff, Calendar, Plus, Trash2,
    Bot, PenTool, Briefcase, LayoutDashboard, Phone, PieChart, Calculator,
    Receipt, Coins, User, Shield
} from 'lucide-react';
import { getAuthHeader } from "../utils/auth"; 

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

// --- 定数定義 ---

// デフォルトのサマリー配置
const DEFAULT_SUMMARY_LAYOUT = {
    top: ['sales_sum', 'sales_forecast', 'gross_profit_sum', 'gross_profit_forecast'],
    middle: ['appointments_sum', 'appointments_forecast', 'orders_sum', 'orders_forecast'],
    bottom: ['rate_appointment_tel', 'rate_order', 'operating_profit', 'operating_profit_forecast']
};

// 期間モードの定義
const VIEW_MODES = [
    { id: 'month', label: '単月' },
    { id: 'quarter', label: '四半期' },
    { id: 'half_year', label: '半期' },
    { id: 'year', label: '年間' },
];

// --- コンポーネント定義 ---

// 1. KPIカード
const KpiCard = ({ title, value, subValue, trend, icon: Icon, colorClass, isEmpty, isPrivate }) => (
    <div className={`p-4 rounded-xl shadow-sm border flex flex-col justify-between hover:shadow-md transition-shadow min-h-[120px] relative overflow-hidden ${isEmpty ? 'bg-gray-50 border-dashed border-gray-300' : 'bg-white border-gray-100'}`}>
        {isPrivate ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-gray-50 absolute inset-0 z-10 bg-opacity-90">
                <Shield size={24} className="mb-2 text-gray-300"/>
                <span className="text-xs font-bold">管理者のみ表示</span>
            </div>
        ) : isEmpty ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">未設定</div>
        ) : (
            <>
                <div className="flex justify-between items-start mb-2">
                    <div className={`p-2 rounded-lg ${colorClass} bg-opacity-50 text-gray-700`}>
                        {Icon && <Icon size={20} />}
                    </div>
                    {trend !== undefined && trend !== null && (
                        <div className={`flex items-center text-xs font-bold ${trend > 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {trend > 0 ? <TrendingUp size={14} className="mr-1"/> : <TrendingDown size={14} className="mr-1"/>}
                            {Math.abs(trend)}%
                        </div>
                    )}
                </div>
                <div>
                    <p className="text-xs text-gray-500 font-medium mb-1">{title}</p>
                    <h3 className="text-xl font-bold text-gray-800">{value}</h3>
                    {subValue && <p className="text-[10px] text-gray-400 mt-1">{subValue}</p>}
                </div>
            </>
        )}
    </div>
);

// 2. インライン編集セル (目標値/実績値)
const EditableCell = ({ value, target, onChange, type, suffix, isEditable, onDrillDown }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState(value);

    useEffect(() => { setTempValue(value); }, [value]);

    const handleBlur = () => {
        setIsEditing(false);
        if (tempValue !== value) onChange(tempValue);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleBlur();
    };

    const rate = target > 0 ? Math.round((value / target) * 100) : 0;
    let rateColor = 'bg-blue-100 text-blue-700';
    if (rate >= 100) rateColor = 'bg-yellow-100 text-yellow-700 font-bold';
    if (rate < 50 && target > 0) rateColor = 'bg-red-50 text-red-600';

    if (isEditing) {
        return (
            <input
                autoFocus
                type="number"
                className="w-full p-1 text-right border-2 border-blue-500 rounded shadow-sm outline-none text-sm"
                value={tempValue}
                onChange={(e) => setTempValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
            />
        );
    }

    return (
        <div 
            className={`group relative min-h-[44px] flex flex-col justify-center items-end px-2 py-1 rounded transition-colors ${isEditable ? 'cursor-pointer hover:bg-blue-50' : 'cursor-default'}`}
            onClick={() => isEditable && setIsEditing(true)}
        >
            <div className="flex items-baseline gap-1">
                <span className="font-bold text-gray-800">
                    {Number(value).toLocaleString()}
                </span>
                <span className="text-[10px] text-gray-400">{suffix}</span>
            </div>
            
            {target > 0 && type !== 'calc' && (
                <div className="w-full flex justify-end items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-gray-300">/ {Number(target).toLocaleString()}</span>
                    <span className={`text-[9px] px-1.5 rounded-full ${rateColor}`}>
                        {rate}%
                    </span>
                </div>
            )}
            
            {isEditable && (
                <Edit3 size={10} className="absolute top-1 left-1 text-gray-300 opacity-0 group-hover:opacity-100" />
            )}

            {/* ドリルダウンボタン */}
            {onDrillDown && (
                <button 
                    onClick={(e) => { e.stopPropagation(); onDrillDown(); }} 
                    className="absolute bottom-1 left-1 p-1 text-blue-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity z-10" 
                    title="内訳を見る"
                >
                    <Search size={14}/>
                </button>
            )}
        </div>
    );
};

// 3. ドリルダウンモーダル (修正版: 月別表示対応)
const DrillDownModal = ({ isOpen, onClose, title, data, headers }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl flex flex-col max-h-[80vh]">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <h3 className="font-bold text-lg flex items-center gap-2 text-gray-800">
                        <Search size={18}/> {title}
                    </h3>
                    <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full"><X size={20}/></button>
                </div>
                <div className="p-6 overflow-auto flex-1">
                    {(!data || data.length === 0) ? (
                        <div className="text-center text-gray-500 py-10">
                            <p>詳細データがありません。</p>
                        </div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-100 text-gray-600 sticky top-0">
                                <tr>
                                    {(headers || ['日付', '項目', '値']).map((h, i) => (
                                        <th key={i} className={`p-3 border-b ${i > 0 ? 'text-right' : ''}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {data.map((row, i) => (
                                    <tr key={i} className="hover:bg-blue-50">
                                        <td className="p-3 text-gray-500">{row.col1}</td>
                                        <td className="p-3 font-medium text-gray-800 text-right">{row.col2}</td>
                                        <td className="p-3 text-right font-bold">{row.col3}</td>
                                        {row.col4 && <td className="p-3 text-right">{row.col4}</td>}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="p-4 border-t bg-gray-50 text-right rounded-b-xl">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">閉じる</button>
                </div>
            </div>
        </div>
    );
};

// 4. 実績入力モーダル (日報)
const ReportModal = ({ isOpen, onClose, kpiMaster, onSubmit, initialDate }) => {
    const [date, setDate] = useState(initialDate || new Date().toISOString().split('T')[0]);
    const [inputs, setInputs] = useState({});
    
    const manualKpis = useMemo(() => kpiMaster.filter(k => k.type === 'manual'), [kpiMaster]);

    useEffect(() => {
        if(isOpen) {
            setInputs({});
            setDate(initialDate || new Date().toISOString().split('T')[0]);
        }
    }, [isOpen, initialDate]);

    const handleSubmit = () => {
        const activities = Object.entries(inputs).map(([key, val]) => ({
            kpi_key: key,
            actual_value: Number(val)
        }));
        onSubmit(date, activities);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
            <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <PenTool size={20} className="text-blue-600"/> 実績入力 (日報)
                    </h3>
                    <button onClick={onClose}><X size={20} className="text-gray-400 hover:text-gray-600"/></button>
                </div>

                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-600 mb-1">対象日</label>
                        <input 
                            type="date" 
                            className="w-full border p-2 rounded bg-gray-50 font-bold text-gray-700" 
                            value={date} 
                            onChange={(e) => setDate(e.target.value)} 
                        />
                    </div>

                    <div className="border rounded-lg p-4 space-y-4 bg-gray-50/50">
                        {manualKpis.map(k => (
                            <div key={k.key_name} className="flex justify-between items-center">
                                <label className="font-bold text-gray-700 text-sm">{k.name}</label>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="number" 
                                        className="w-24 border p-2 rounded text-right font-mono text-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="0"
                                        value={inputs[k.key_name] || ''}
                                        onChange={(e) => setInputs({...inputs, [k.key_name]: e.target.value})}
                                    />
                                    <span className="text-xs text-gray-500 w-4">{k.unit}</span>
                                </div>
                            </div>
                        ))}
                        {manualKpis.length === 0 && <p className="text-center text-gray-400 text-sm">手動入力が必要な項目はありません</p>}
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                    <button onClick={onClose} className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded text-sm font-medium">キャンセル</button>
                    <button onClick={handleSubmit} className="px-6 py-2 bg-blue-600 text-white font-bold rounded text-sm shadow-sm hover:bg-blue-700 transition-colors">保存する</button>
                </div>
            </div>
        </div>
    );
};

// 5. 設定モーダル (多機能版: サマリー設定追加)
const ConfigModal = ({ isOpen, onClose, expenses, kpiMaster, currentMonth, summaryLayout, onSaveExpenses, onSaveKpis, onSaveSummary, availableSummaryItems }) => {
    const [activeTab, setActiveTab] = useState('summary');
    
    // 販管費
    const [localExpenses, setLocalExpenses] = useState([]);
    const [newExpName, setNewExpName] = useState("");
    const [newExpAmount, setNewExpAmount] = useState("");

    // KPI
    const [localKpis, setLocalKpis] = useState([]);

    // サマリー配置
    const [localSummary, setLocalSummary] = useState(DEFAULT_SUMMARY_LAYOUT);

    useEffect(() => {
        if (isOpen) {
            setLocalExpenses(Array.isArray(expenses) ? [...expenses] : []);
            setLocalKpis(Array.isArray(kpiMaster) ? JSON.parse(JSON.stringify(kpiMaster)) : []);
            setLocalSummary(summaryLayout ? JSON.parse(JSON.stringify(summaryLayout)) : DEFAULT_SUMMARY_LAYOUT);
        }
    }, [isOpen, expenses, kpiMaster, summaryLayout]);

    // 販管費操作
    const handleAddExpense = () => {
        if (!newExpName || !newExpAmount) return;
        setLocalExpenses([...localExpenses, { id: Date.now(), expense_name: newExpName, amount: Number(newExpAmount) }]);
        setNewExpName("");
        setNewExpAmount("");
    };
    const handleDeleteExpense = (id) => {
        setLocalExpenses(localExpenses.filter(e => e.id !== id));
    };
    const totalExpenses = localExpenses.reduce((sum, e) => sum + Number(e.amount), 0);

    // KPI操作
    const toggleKpiVisible = (keyName) => {
        setLocalKpis(prev => prev.map(k => 
            k.key_name === keyName ? { ...k, is_visible: !k.is_visible } : k
        ));
    };
    const handleWeightChange = (keyName, val) => {
        setLocalKpis(prev => prev.map(k => 
            k.key_name === keyName ? { ...k, weight: Number(val) } : k
        ));
    };

    // サマリー設定操作
    const handleSummaryChange = (section, index, value) => {
        const newLayout = { ...localSummary };
        newLayout[section][index] = value;
        setLocalSummary(newLayout);
    };

    // 保存処理
    const handleSave = () => {
        onSaveExpenses(localExpenses);
        onSaveKpis(localKpis);
        onSaveSummary(localSummary);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="flex border-b bg-gray-50">
                    <button onClick={() => setActiveTab('summary')} className={`flex-1 p-4 text-sm font-bold ${activeTab === 'summary' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}>サマリー配置</button>
                    <button onClick={() => setActiveTab('expenses')} className={`flex-1 p-4 text-sm font-bold ${activeTab === 'expenses' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}>月間販管費</button>
                    <button onClick={() => setActiveTab('kpi_visible')} className={`flex-1 p-4 text-sm font-bold ${activeTab === 'kpi_visible' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}>表示項目設定</button>
                    <button onClick={() => setActiveTab('kpi_weight')} className={`flex-1 p-4 text-sm font-bold ${activeTab === 'kpi_weight' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}>評価ウェイト</button>
                </div>

                <div className="p-6 overflow-y-auto flex-1 bg-white">
                    {/* サマリー設定 */}
                    {activeTab === 'summary' && (
                        <div className="space-y-8">
                            {['top', 'middle', 'bottom'].map(section => (
                                <div key={section} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <h4 className="text-sm font-bold text-gray-800 mb-3 border-b pb-2 uppercase tracking-wider">
                                        {section === 'top' ? '上段エリア' : section === 'middle' ? '中段エリア' : '下段エリア'}
                                    </h4>
                                    <div className="grid grid-cols-4 gap-4">
                                        {[0, 1, 2, 3].map(idx => (
                                            <div key={idx}>
                                                <select 
                                                    className="w-full border rounded p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                                    value={localSummary[section][idx] || ""}
                                                    onChange={(e) => handleSummaryChange(section, idx, e.target.value)}
                                                >
                                                    <option value="">(表示なし)</option>
                                                    <optgroup label="合計">
                                                        {availableSummaryItems.filter(i => i.category === 'sum').map(item => (<option key={item.key} value={item.key}>{item.label}</option>))}
                                                    </optgroup>
                                                    <optgroup label="着地見込み">
                                                        {availableSummaryItems.filter(i => i.category === 'forecast').map(item => (<option key={item.key} value={item.key}>{item.label}</option>))}
                                                    </optgroup>
                                                    <optgroup label="率・その他">
                                                        {availableSummaryItems.filter(i => i.category === 'rate' || i.category === 'profit').map(item => (<option key={item.key} value={item.key}>{item.label}</option>))}
                                                    </optgroup>
                                                </select>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 販管費設定 */}
                    {activeTab === 'expenses' && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="text-sm font-bold text-gray-800">{currentMonth} の販管費内訳</h4>
                            </div>
                            
                            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                                {localExpenses.map(exp => (
                                    <div key={exp.id} className="flex gap-2 items-center">
                                        <input 
                                            type="text" 
                                            className="flex-1 border p-2 rounded text-sm bg-gray-50"
                                            value={exp.expense_name}
                                            readOnly 
                                        />
                                        <div className="relative w-40">
                                            <input 
                                                type="number" 
                                                className="w-full border p-2 rounded text-sm text-right pr-8 font-bold"
                                                value={exp.amount}
                                                readOnly
                                            />
                                            <span className="absolute right-2 top-2 text-xs text-gray-400">円</span>
                                        </div>
                                        <button onClick={() => handleDeleteExpense(exp.id)} className="p-2 text-gray-400 hover:text-red-500"><Trash2 size={16}/></button>
                                    </div>
                                ))}
                                {localExpenses.length === 0 && <p className="text-center text-gray-400 text-sm py-4">登録なし</p>}
                            </div>

                            <div className="flex gap-2 pt-4 border-t mt-4">
                                <input 
                                    className="flex-1 border p-2 rounded text-sm" 
                                    placeholder="新規項目名 (例: 家賃)" 
                                    value={newExpName} 
                                    onChange={e => setNewExpName(e.target.value)} 
                                />
                                <input 
                                    type="number" 
                                    className="w-32 border p-2 rounded text-sm" 
                                    placeholder="金額" 
                                    value={newExpAmount} 
                                    onChange={e => setNewExpAmount(e.target.value)} 
                                />
                                <button onClick={handleAddExpense} disabled={!newExpName || !newExpAmount} className="bg-green-600 text-white px-4 rounded hover:bg-green-700 disabled:bg-gray-300 font-bold text-sm flex items-center gap-1">
                                    <Plus size={16}/> 追加
                                </button>
                            </div>
                            
                            <div className="border-t pt-3 flex justify-between items-center bg-gray-50 p-3 rounded mt-4">
                                <span className="font-bold text-gray-600">合計 (固定費)</span>
                                <span className="font-black text-lg text-gray-800">¥{totalExpenses.toLocaleString()}</span>
                            </div>
                        </div>
                    )}

                    {/* 表示項目設定 */}
                    {activeTab === 'kpi_visible' && (
                        <div className="grid grid-cols-2 gap-3">
                            {localKpis.map((k) => (
                                <div 
                                    key={k.key_name} 
                                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${k.is_visible ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200 hover:bg-gray-50'}`} 
                                    onClick={() => toggleKpiVisible(k.key_name)}
                                >
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 ${k.is_visible ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300'}`}>
                                        {k.is_visible && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>}
                                    </div>
                                    <div className="flex-1">
                                        <span className={`block text-sm font-bold ${k.is_visible ? 'text-blue-800' : 'text-gray-500'}`}>{k.name}</span>
                                        <span className="text-xs text-gray-400">{k.type === 'system' ? '自動連携' : k.type === 'manual' ? '手動入力' : '自動計算'}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ウェイト設定 */}
                    {activeTab === 'kpi_weight' && (
                        <div className="space-y-2">
                            <p className="text-xs text-gray-500 mb-4">※ 各項目の重要度(%)を設定します。合計値が100になる必要はありませんが、バランスよく設定してください。</p>
                            {localKpis.filter(k => k.is_visible).map((k) => (
                                <div key={k.key_name} className="flex items-center gap-4 p-2 hover:bg-gray-50 rounded">
                                    <span className="w-40 font-medium text-gray-700 text-sm">{k.name}</span>
                                    <input 
                                        type="range" min="0" max="100" step="5" 
                                        value={k.weight || 0} 
                                        onChange={(e) => handleWeightChange(k.key_name, e.target.value)}
                                        className="flex-1 accent-blue-600"
                                    />
                                    <span className="w-12 text-right font-mono font-bold">{k.weight}%</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded text-sm">キャンセル</button>
                    <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white font-bold rounded hover:bg-blue-700 shadow text-sm">設定を保存</button>
                </div>
            </div>
        </div>
    );
};

// --- メインコンポーネント ---
export default function SalesAnalytics() {
    const { user } = useOutletContext(); 

    // 🚀 修正: OEM社長(admin)やスタッフがプルダウンで選択したクライアントを反映
    const clientId = useMemo(() => (
      user?.role === "admin" || user?.role === "admin_staff" 
        ? localStorage.getItem("selectedClientId") || "" 
        : user?.client_id
    ), [user]);

    const isAdmin = user?.role === 'admin' || user?.role === 'client_admin' || user?.role === 'admin_staff';

    // State
    const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
    const [viewMode, setViewMode] = useState('month'); // 'month' | 'quarter' | 'half_year' | 'year'
    
    const [kpiMaster, setKpiMaster] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [graphData, setGraphData] = useState([]); // サーバーから来た生データ(日次)
    const [loading, setLoading] = useState(true);
    const [isReportMode, setIsReportMode] = useState(false);
    
    // サマリー配置設定
    const [summaryLayout, setSummaryLayout] = useState(DEFAULT_SUMMARY_LAYOUT);

    // Modals
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [drillDown, setDrillDown] = useState({ isOpen: false, title: '', data: null, headers: [] });

    // Graph Settings
    const [graphPeriod, setGraphPeriod] = useState('daily');
    const [graphMetric, setGraphMetric] = useState('amount'); // 'amount' | 'action'
    const [graphUser, setGraphUser] = useState('all'); // 'all' | user_id

    // 期間計算ヘルパー
    const getDateRange = useCallback(() => {
        const [yearStr, monthStr] = currentMonth.split('-');
        const year = parseInt(yearStr);
        const month = parseInt(monthStr); // 1-12

        let start, end;

        if (viewMode === 'month') {
            start = new Date(year, month - 1, 1);
            end = new Date(year, month, 0);
        } else if (viewMode === 'quarter') {
            // 現在の月が含まれる四半期 (1-3, 4-6, 7-9, 10-12)
            const quarterStartMonth = Math.floor((month - 1) / 3) * 3;
            start = new Date(year, quarterStartMonth, 1);
            end = new Date(year, quarterStartMonth + 3, 0);
        } else if (viewMode === 'half_year') {
            // 上期(1-6) or 下期(7-12)
            const halfStartMonth = month <= 6 ? 0 : 6;
            start = new Date(year, halfStartMonth, 1);
            end = new Date(year, halfStartMonth + 6, 0);
        } else if (viewMode === 'year') {
            start = new Date(year, 0, 1);
            end = new Date(year, 11, 31);
        }

        // YYYY-MM-DD 形式に変換
        const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return { startDate: formatDate(start), endDate: formatDate(end) };
    }, [currentMonth, viewMode]);

    // データ取得
    const fetchData = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const { startDate, endDate } = getDateRange();

            // KPIマスタ取得
            const kpiRes = await fetch(`${API_URL}/api/analytics/kpi-master?client_id=${clientId}`, { headers: getAuthHeader() });
            const kpiJson = await kpiRes.json();
            if (kpiJson.success) setKpiMaster(kpiJson.kpis);

            // ダッシュボードデータ取得 (期間指定)
            const dashRes = await fetch(`${API_URL}/api/analytics/dashboard?client_id=${clientId}&startDate=${startDate}&endDate=${endDate}`, { headers: getAuthHeader() });
            const dashJson = await dashRes.json();
            
            if (dashJson.success) {
                setTableData(dashJson.data);
                setExpenses(Array.isArray(dashJson.expenses) ? dashJson.expenses : []);
                setGraphData(Array.isArray(dashJson.graphData) ? dashJson.graphData : []);
            }
        } catch (err) {
            console.error("データ取得エラー:", err);
        } finally {
            setLoading(false);
        }
    }, [clientId, getDateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // ビューモード変更時のハンドラ
    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        // モードを変えたら、グラフの表示単位も自然なものに変える
        if (mode === 'month') setGraphPeriod('daily');
        else if (mode === 'quarter' || mode === 'half_year') setGraphPeriod('weekly');
        else if (mode === 'year') setGraphPeriod('monthly');
    };

    // グラフデータの集計 (日次データを週次/月次にまとめる)
    const aggregatedGraphData = useMemo(() => {
        if (!graphData || graphData.length === 0) return [];

        if (graphPeriod === 'daily') {
            // 日次はそのまま使う (表示用に日付フォーマットだけ調整してもよい)
            return graphData;
        }

        const aggregated = {};

        graphData.forEach(day => {
            const dateObj = new Date(day.date);
            let key;
            let label;

            if (graphPeriod === 'weekly') {
                // 週の開始日(日曜日)をキーにする
                const d = new Date(day.date);
                const dayNum = d.getDay();
                const diff = d.getDate() - dayNum; 
                const weekStart = new Date(d.setDate(diff));
                key = weekStart.toISOString().slice(0, 10);
                label = `${weekStart.getMonth()+1}/${weekStart.getDate()}週`;
            } else if (graphPeriod === 'monthly') {
                // YYYY-MM をキーにする
                key = day.date.slice(0, 7);
                label = `${dateObj.getMonth()+1}月`;
            }

            if (!aggregated[key]) {
                aggregated[key] = {
                    name: label,
                    date: key, // ソート用
                    sales: 0,
                    gross_profit: 0,
                    appointments: 0,
                    orders: 0,
                    calls: 0
                };
            }

            // 加算
            aggregated[key].sales += day.sales;
            aggregated[key].gross_profit += day.gross_profit;
            aggregated[key].appointments += day.appointments;
            aggregated[key].orders += day.orders;
            aggregated[key].calls += day.calls;
        });

        // 日付順にソートして配列に戻す
        return Object.values(aggregated).sort((a, b) => a.date.localeCompare(b.date));

    }, [graphData, graphPeriod]);


    // サマリー用項目の定義リスト (kpiMasterから生成)
    const availableSummaryItems = useMemo(() => {
        const items = [];
        
        kpiMaster.forEach(k => {
            if (k.type !== 'calc') {
                items.push({ key: `${k.key_name}_sum`, label: `${k.name}合計`, category: 'sum', unit: k.unit, sourceKey: k.key_name });
                items.push({ key: `${k.key_name}_forecast`, label: `着地${k.name}`, category: 'forecast', unit: k.unit, sourceKey: k.key_name });
            } else {
                items.push({ key: `${k.key_name}`, label: `${k.name}(全体)`, category: 'rate', unit: k.unit, sourceKey: k.key_name });
            }
        });

        items.push({ key: 'operating_profit', label: '営業利益', category: 'profit', unit: '円' });
        items.push({ key: 'operating_profit_forecast', label: '着地営業利益', category: 'profit', unit: '円' });

        return items;
    }, [kpiMaster]);

    // サマリー値の計算
    const summaryValues = useMemo(() => {
        const sums = {};
        const forecast = {};
        
        // 着地見込み計算:
        // 現在の日付が、選択期間(startDate~endDate)の何%経過したかで按分する
        const { startDate, endDate } = getDateRange();
        const start = new Date(startDate);
        const end = new Date(endDate);
        const now = new Date();
        
        const totalDuration = end - start;
        let elapsed = now - start;
        if (elapsed < 0) elapsed = 0; // 未来の期間を選択中
        if (elapsed > totalDuration) elapsed = totalDuration; // 過去の期間を選択中

        // 経過率 (0.0 ~ 1.0)
        let progressRate = totalDuration > 0 ? elapsed / totalDuration : 1;
        // 月初などは0になるのを防ぐため最小値を設定
        progressRate = Math.max(progressRate, 0.03); 

        const forecastRate = 1 / progressRate;

        // 1. 各KPIの合計と着地を計算
        kpiMaster.forEach(k => {
            if (k.type !== 'calc') {
                const total = tableData.reduce((sum, u) => sum + (u.metrics[k.key_name]?.actual || 0), 0);
                sums[k.key_name] = total;
                // 過去の期間なら着地＝実績
                forecast[k.key_name] = end < now ? total : Math.floor(total * forecastRate);
            }
        });

        // 2. 販管費
        const totalExpenses = expenses.reduce((acc, e) => acc + Number(e.amount), 0);

        // 3. 表示用オブジェクトの構築
        const values = {};

        availableSummaryItems.forEach(item => {
            if (item.category === 'sum') {
                values[item.key] = (sums[item.sourceKey] || 0).toLocaleString();
                if (item.unit === '円') values[item.key] = `¥${values[item.key]}`;
                else values[item.key] += item.unit;
            }
            else if (item.category === 'forecast') {
                values[item.key] = (forecast[item.sourceKey] || 0).toLocaleString();
                if (item.unit === '円') values[item.key] = `¥${values[item.key]}`;
                else values[item.key] += item.unit;
            }
            else if (item.category === 'rate') {
                // 率の計算ロジック (簡易実装: 全体の平均)
                let rate = 0;
                // ※ 本来はKPI定義のマスタに計算式を持たせるべきだが、ここではハードコード
                if (item.sourceKey === 'rate_appointment_tel') {
                    rate = sums['manual_calls'] > 0 ? (sums['appointments'] / sums['manual_calls'] * 100) : 0;
                } else if (item.sourceKey === 'rate_order') {
                    rate = sums['negotiations'] > 0 ? (sums['orders'] / sums['negotiations'] * 100) : 0;
                }
                values[item.key] = `${rate.toFixed(1)}%`;
            }
        });

        // 利益 (粗利 - 販管費)
        const gross = sums['gross_profit'] || 0;
        const grossForecast = forecast['gross_profit'] || 0;
        
        values['operating_profit'] = `¥${(gross - totalExpenses).toLocaleString()}`;
        values['operating_profit_forecast'] = `¥${(grossForecast - totalExpenses).toLocaleString()}`;

        return values;
    }, [tableData, expenses, kpiMaster, availableSummaryItems, getDateRange]);

    // サマリーカードのスタイルヘルパー
    const getItemStyle = (key) => {
        if (!key) return { icon: null, color: 'bg-gray-100' };
        if (key.includes('sales')) return { icon: DollarSign, color: 'bg-blue-100' };
        if (key.includes('gross') || key.includes('profit')) return { icon: DollarSign, color: 'bg-green-100' };
        if (key.includes('forecast')) return { icon: TrendingUp, color: 'bg-orange-50' };
        if (key.includes('rate')) return { icon: PieChart, color: 'bg-indigo-100' };
        if (key.includes('calls') || key.includes('appointments')) return { icon: Phone, color: 'bg-purple-100' };
        if (key.includes('orders') || key.includes('negotiations')) return { icon: Briefcase, color: 'bg-orange-100' };
        return { icon: Activity, color: 'bg-gray-100' };
    };

    // 総合スコア計算
    const processedTableData = useMemo(() => {
        return tableData.map(user => {
            let totalScore = 0;
            let totalWeight = 0;

            kpiMaster.forEach(kpi => {
                if (kpi.weight > 0 && kpi.is_visible) {
                    const m = user.metrics[kpi.key_name];
                    if (m) {
                        let achievement = 0;
                        if (m.target > 0) {
                            achievement = (m.actual / m.target) * 100;
                        }
                        achievement = Math.min(achievement, 200);
                        
                        totalScore += achievement * (kpi.weight / 100);
                        totalWeight += kpi.weight;
                    }
                }
            });

            // ウェイト合計で正規化
            const normalizedScore = totalWeight > 0 ? Math.round(totalScore * (100 / totalWeight)) : 0;

            return { ...user, totalScore: normalizedScore };
        }).sort((a, b) => b.totalScore - a.totalScore);
    }, [tableData, kpiMaster]);

    // 目標値更新ハンドラ
    const handleTargetUpdate = async (userId, kpiKey, newVal) => {
        if (!isAdmin) return;
        setTableData(prev => prev.map(u => {
            if (u.user_id === userId) {
                return {
                    ...u,
                    metrics: { ...u.metrics, [kpiKey]: { ...u.metrics[kpiKey], target: Number(newVal) } }
                };
            }
            return u;
        }));

        try {
            await fetch(`${API_URL}/api/analytics/goals`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({
                    client_id: clientId,
                    year_month: currentMonth,
                    goals: [{ user_id: userId, kpi_key: kpiKey, target_value: Number(newVal) }]
                })
            });
        } catch (e) {
            console.error(e);
            alert("保存に失敗しました");
            fetchData();
        }
    };

    // 日報保存ハンドラ
    const handleReportSubmit = async (date, activities) => {
        try {
            const res = await fetch(`${API_URL}/api/analytics/daily-activities`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ client_id: clientId, activity_date: date, activities })
            });
            if (res.ok) {
                alert("実績を保存しました");
                fetchData();
            } else {
                throw new Error("保存失敗");
            }
        } catch (e) {
            alert("保存中にエラーが発生しました");
        }
    };

    // 販管費/KPI保存ハンドラ
    const handleExpensesSave = async (newExpenses) => {
        try {
            await fetch(`${API_URL}/api/analytics/expenses`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({
                    client_id: clientId,
                    year_month: currentMonth,
                    expenses: newExpenses
                })
            });
            setExpenses(newExpenses);
        } catch (e) {
            console.error(e);
            alert("保存エラー");
        }
    };

    const handleKpiSave = async (newKpis) => {
        setKpiMaster(newKpis); 
    };

    const handleSummarySave = (newLayout) => {
        setSummaryLayout(newLayout);
    };

    // ドリルダウン表示 (修正: 月別予実の表示)
    const openDrillDown = (user, kpiKey, kpiName, kpiUnit) => {
        const monthlyMetrics = user.monthly_metrics || {};
        
        // 月別データの生成 (ソート済み)
        const drillData = Object.keys(monthlyMetrics).sort().map(ym => {
            const data = monthlyMetrics[ym][kpiKey] || { target: 0, actual: 0 };
            const achievement = data.target > 0 ? Math.round((data.actual / data.target) * 100) : 0;
            return {
                col1: ym, // 年月
                col2: `目標: ${data.target.toLocaleString()}${kpiUnit}`,
                col3: `実績: ${data.actual.toLocaleString()}${kpiUnit}`,
                col4: `(${achievement}%)`
            };
        });

        if (drillData.length === 0) {
            setDrillDown({ isOpen: true, title: `${user.user_name} - ${kpiName}`, data: [], headers: [] });
        } else {
            setDrillDown({ 
                isOpen: true, 
                title: `${user.user_name} - ${kpiName} (月別推移)`, 
                data: drillData,
                headers: ['年月', '目標', '実績', '達成率']
            });
        }
    };

    if (loading && tableData.length === 0) return <div className="min-h-screen flex items-center justify-center text-gray-500">データを読み込んでいます...</div>;

    return (
        <div className={`min-h-screen bg-gray-50 ${isReportMode ? 'p-4 sm:p-8 bg-white' : 'p-3 sm:p-6'}`}>
            
            {/* ヘッダーエリア */}
            {!isReportMode && (
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="text-2xl font-bold text-gray-800">売上分析・予実管理</h1>
                        {isAdmin ? (
                            <span className="px-2 py-1 text-xs rounded bg-indigo-100 text-indigo-700 font-bold border border-indigo-200">管理者ビュー</span>
                        ) : (
                            <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 font-bold border border-green-200">スタッフビュー</span>
                        )}
                    </div>
                    <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 md:w-auto md:flex md:flex-wrap md:items-center md:justify-end">
                        
                        {/* 期間モード選択 (管理者のみ) */}
                        {isAdmin && (
                            <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 p-1 overflow-x-auto">
                                {VIEW_MODES.map(mode => (
                                    <button 
                                        key={mode.id}
                                        onClick={() => handleViewModeChange(mode.id)}
                                        className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${viewMode === mode.id ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`}
                                    >
                                        {mode.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="flex items-center justify-center bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                            <button className="p-1 hover:bg-gray-100 rounded"><ChevronLeft size={20} className="text-gray-600"/></button>
                            <input 
                                type="month" 
                                className="border-none outline-none text-lg font-bold text-gray-700 bg-transparent px-2 w-32 text-center cursor-pointer"
                                value={currentMonth}
                                onChange={(e) => setCurrentMonth(e.target.value)}
                            />
                            <button className="p-1 hover:bg-gray-100 rounded"><ChevronRight size={20} className="text-gray-600"/></button>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center">
                            <button onClick={() => setReportModalOpen(true)} className="col-span-3 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 shadow-sm transition font-bold text-sm sm:col-span-1">
                                <PenTool size={16} /> 実績入力
                            </button>

                            {isAdmin && (
                                <button onClick={() => setConfigModalOpen(true)} className="flex items-center justify-center gap-2 bg-white text-gray-600 px-3 py-2 rounded-lg border hover:bg-gray-50 shadow-sm transition text-sm">
                                    <Settings size={18} /> 設定
                                </button>
                            )}
                            
                            <button onClick={() => setIsReportMode(!isReportMode)} className="flex items-center justify-center gap-2 bg-white text-gray-600 px-3 py-2 rounded-lg border hover:bg-gray-50 shadow-sm transition text-sm" title="印刷用モード">
                                <Printer size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* サマリーカードエリア (設定に基づいて表示) */}
            <div className="space-y-4 mb-8">
                {['top', 'middle', 'bottom'].map(section => (
                    <div key={section} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                        {summaryLayout[section].map((key, idx) => {
                            const itemDef = availableSummaryItems.find(i => i.key === key);
                            const style = getItemStyle(key);
                            // 利益などの機密情報は管理者のみ表示
                            const isSensitive = key && key.includes('profit');
                            
                            return (
                                <KpiCard 
                                    key={`${section}-${idx}`}
                                    title={itemDef ? itemDef.label : null}
                                    value={itemDef ? summaryValues[key] : null}
                                    subValue={null}
                                    icon={style.icon}
                                    colorClass={style.color}
                                    isEmpty={!itemDef}
                                    isPrivate={isSensitive && !isAdmin}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* グラフエリア */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-8">
                <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center mb-6 gap-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                        {/* 指標切り替え */}
                        <div className="grid grid-cols-2 bg-gray-100 p-1 rounded-lg">
                            <button 
                                onClick={() => setGraphMetric('amount')} 
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${graphMetric === 'amount' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
                            >
                                <DollarSign size={14}/> 売上・粗利
                            </button>
                            <button 
                                onClick={() => setGraphMetric('action')} 
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${graphMetric === 'action' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500'}`}
                            >
                                <Activity size={14}/> 行動量
                            </button>
                        </div>

                        <h3 className="text-lg font-bold text-gray-800 hidden md:block">
                            推移グラフ ({viewMode === 'month' ? currentMonth : VIEW_MODES.find(m=>m.id===viewMode)?.label})
                        </h3>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        {/* 担当者絞り込み (UIのみ) */}
                        <div className="relative">
                            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                            <select 
                                className="w-full pl-9 pr-4 py-2 sm:py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
                                value={graphUser}
                                onChange={(e) => setGraphUser(e.target.value)}
                            >
                                <option value="all">全社合計</option>
                                {tableData.map(u => (
                                    <option key={u.user_id} value={u.user_id}>{u.user_name}</option>
                                ))}
                            </select>
                        </div>

                        {/* 期間切り替え */}
                        <div className="grid grid-cols-3 bg-gray-100 p-1 rounded-lg">
                            {[{ id: 'daily', label: '日次' }, { id: 'weekly', label: '週次' }, { id: 'monthly', label: '月次' }].map(tab => (
                                <button key={tab.id} onClick={() => setGraphPeriod(tab.id)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${graphPeriod === tab.id ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="h-64 sm:h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        {aggregatedGraphData.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-gray-400">グラフデータがありません</div>
                        ) : graphMetric === 'amount' ? (
                            <AreaChart data={aggregatedGraphData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs><linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.2}/><stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/></linearGradient></defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="name" tick={{fontSize: 12}} axisLine={false} tickLine={false} interval={graphPeriod==='daily'?2:0} />
                                <YAxis tickFormatter={(v)=> `${v/10000}万`} tick={{fontSize: 12}} axisLine={false} tickLine={false}/>
                                <Tooltip formatter={(v) => `¥${v.toLocaleString()}`} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}/>
                                <Area type="monotone" dataKey="sales" stroke="#3B82F6" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" name="売上" />
                                <Area type="monotone" dataKey="gross_profit" stroke="#10B981" strokeWidth={3} fillOpacity={0} name="粗利" />
                            </AreaChart>
                        ) : (
                            <ComposedChart data={aggregatedGraphData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="name" tick={{fontSize: 12}} axisLine={false} tickLine={false} interval={graphPeriod==='daily'?2:0} />
                                <YAxis tick={{fontSize: 12}} axisLine={false} tickLine={false}/>
                                <Tooltip formatter={(v) => `${v}件`} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}/>
                                <Bar dataKey="calls" fill="#A78BFA" radius={[4, 4, 0, 0]} name="架電数" barSize={20}/>
                                <Line type="monotone" dataKey="appointments" stroke="#F59E0B" strokeWidth={3} dot={{r: 4}} name="アポ数"/>
                                <Line type="monotone" dataKey="orders" stroke="#10B981" strokeWidth={3} dot={{r: 4}} name="受注数"/>
                            </ComposedChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </div>

            {/* メインテーブル (予実管理シート) */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 sm:p-6 border-b flex flex-col gap-3 bg-white sm:flex-row sm:justify-between sm:items-end">
                    <div>
                        <h3 className="font-bold text-xl text-gray-800">担当者別 予実・評価シート</h3>
                        <p className="text-xs text-gray-400 mt-1">
                            ※ <Bot size={12} className="inline text-blue-500"/> は自動連携、<PenTool size={12} className="inline text-green-600"/> は手動入力です。
                            {isAdmin && ' 目標値クリックで編集可能。虫眼鏡で月別内訳を確認できます。'}
                        </p>
                    </div>
                    <button className="w-full text-sm text-blue-600 hover:bg-blue-50 px-3 py-2 rounded transition flex items-center justify-center gap-1 sm:w-auto sm:py-1"><Download size={16}/> CSV出力</button>
                </div>
                
                <div className="divide-y divide-gray-100 md:hidden">
                    {processedTableData.map((member, idx) => (
                        <div key={`${member.user_id}-mobile`} className="p-4 bg-white">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className={`w-9 h-9 rounded-full flex shrink-0 items-center justify-center text-sm font-bold text-white shadow-sm ${idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' : idx === 2 ? 'bg-gradient-to-br from-orange-300 to-orange-400' : 'bg-blue-100 text-blue-600'}`}>
                                        {idx + 1}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="truncate font-bold text-gray-800">{member.user_name}</div>
                                    </div>
                                </div>
                                <span className={`shrink-0 inline-block px-3 py-1 rounded-full font-bold text-lg ${member.totalScore >= 100 ? 'text-green-600 bg-green-50' : 'text-blue-600 bg-blue-50'}`}>
                                    {member.totalScore} <span className="text-xs font-normal">pt</span>
                                </span>
                            </div>
                            <div className="mt-4 space-y-3">
                                {kpiMaster.filter(k => k.is_visible).map(kpi => {
                                    const m = member.metrics[kpi.key_name] || { actual: 0, target: 0 };
                                    return (
                                        <div key={kpi.key_name} className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                                            <div className="mb-2 flex items-center justify-between gap-2">
                                                <div className="flex items-center gap-1 text-xs font-bold text-gray-600">
                                                    {kpi.type === 'system' ? <Bot size={14} className="text-blue-400"/> :
                                                     kpi.type === 'manual' ? <PenTool size={14} className="text-green-500"/> :
                                                     <Activity size={14} className="text-orange-400"/>}
                                                    <span>{kpi.name}</span>
                                                </div>
                                                <span className="text-[10px] text-gray-400">({kpi.unit})</span>
                                            </div>
                                            {kpi.type !== 'calc' ? (
                                                <EditableCell
                                                    value={m.actual}
                                                    target={m.target}
                                                    suffix={kpi.unit}
                                                    isEditable={isAdmin}
                                                    type={kpi.type}
                                                    onChange={(val) => handleTargetUpdate(member.user_id, kpi.key_name, val)}
                                                    onDrillDown={() => openDrillDown(member, kpi.key_name, kpi.name, kpi.unit)}
                                                />
                                            ) : (
                                                <div className="text-right px-2 py-2 font-bold text-gray-700 text-base">{m.actual}{kpi.unit}</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="hidden overflow-x-auto md:block">
                    <table className="w-full text-sm text-left border-collapse">
                        <thead className="bg-gray-50 text-gray-600 font-medium border-b text-xs uppercase">
                            <tr>
                                <th className="px-6 py-4 min-w-[180px] bg-gray-50 sticky left-0 z-20">担当者</th>
                                <th className="px-4 py-4 text-center min-w-[100px]">総合スコア</th>
                                {kpiMaster.filter(k => k.is_visible).map(k => (
                                    <th key={k.key_name} className="px-4 py-4 min-w-[140px] text-right whitespace-nowrap">
                                        <div className="flex items-center justify-end gap-1 mb-1">
                                            {k.type === 'system' ? <Bot size={14} className="text-blue-400"/> : 
                                             k.type === 'manual' ? <PenTool size={14} className="text-green-500"/> : 
                                             <Activity size={14} className="text-orange-400"/>}
                                            <span>{k.name}</span>
                                        </div>
                                        <span className="text-[10px] text-gray-400 font-normal block">({k.unit})</span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {processedTableData.map((user, idx) => (
                                <tr key={user.user_id} className={`hover:bg-gray-50 transition-colors group ${user.user_id === user?.id ? 'bg-blue-50/20' : ''}`}>
                                    <td className="px-6 py-4 bg-white sticky left-0 z-10 border-r border-gray-100 group-hover:bg-gray-50 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-sm ${idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' : idx === 2 ? 'bg-gradient-to-br from-orange-300 to-orange-400' : 'bg-blue-100 text-blue-600'}`}>
                                                {idx + 1}
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-800">{user.user_name}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-center bg-gray-50/30">
                                        <span className={`inline-block px-3 py-1 rounded-full font-bold text-lg ${user.totalScore >= 100 ? 'text-green-600 bg-green-50' : 'text-blue-600 bg-blue-50'}`}>
                                            {user.totalScore} <span className="text-xs font-normal">pt</span>
                                        </span>
                                    </td>
                                    {kpiMaster.filter(k => k.is_visible).map(kpi => {
                                        const m = user.metrics[kpi.key_name] || { actual: 0, target: 0 };
                                        return (
                                            <td key={kpi.key_name} className="px-4 py-2 border-l border-gray-50 relative">
                                                {kpi.type !== 'calc' ? (
                                                    <EditableCell 
                                                        value={m.actual} 
                                                        target={m.target} 
                                                        suffix={kpi.unit} 
                                                        isEditable={isAdmin} // 管理者のみ目標編集可
                                                        type={kpi.type} 
                                                        onChange={(val) => handleTargetUpdate(user.user_id, kpi.key_name, val)}
                                                        onDrillDown={() => openDrillDown(user, kpi.key_name, kpi.name, kpi.unit)} // ドリルダウン
                                                    />
                                                ) : (
                                                    <div className="text-right px-2 py-2 font-bold text-gray-700 text-base">{m.actual}{kpi.unit}</div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* モーダル群 */}
            <ReportModal 
                isOpen={reportModalOpen} 
                onClose={() => setReportModalOpen(false)} 
                kpiMaster={kpiMaster} 
                onSubmit={handleReportSubmit}
                initialDate={undefined}
            />
            
            <ConfigModal 
                isOpen={configModalOpen} 
                onClose={() => setConfigModalOpen(false)} 
                expenses={expenses}
                kpiMaster={kpiMaster}
                currentMonth={currentMonth}
                summaryLayout={summaryLayout}
                availableSummaryItems={availableSummaryItems}
                onSaveExpenses={handleExpensesSave}
                onSaveKpis={handleKpiSave}
                onSaveSummary={handleSummarySave}
            />
            
            <DrillDownModal 
                isOpen={drillDown.isOpen} 
                onClose={() => setDrillDown({ ...drillDown, isOpen: false })} 
                title={drillDown.title} 
                data={drillDown.data}
                headers={drillDown.headers}
            />
        </div>
    );
}
